import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { categoryService } from '@/services/categories';
import type { 
  Category,
  CreateCategoryParams,
  UpdateCategoryParams,
  DeleteCategoryParams,
  UpdateCategoryOrderParams
} from '@/features/categories/types';

export function useCategories(
  providerId: number, 
  versionId: number
) {
  return useQuery({
    queryKey: ['categories', providerId, versionId],
    queryFn: () => categoryService.getCategories(providerId, versionId)
  });
}

export function useCategoryHierarchy(providerId: number, versionId: number) {
  return useQuery({
    queryKey: ['category-hierarchy', providerId, versionId],
    queryFn: () => categoryService.getCategoryHierarchy(providerId, versionId)
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  
  return useMutation<Category, Error, CreateCategoryParams>({
    mutationFn: (params: CreateCategoryParams) => categoryService.createCategory(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['categories', variables.providerId, variables.versionId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['category-hierarchy', variables.providerId, variables.versionId] 
      });
      toast.success('Category created successfully');
    }
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  
  return useMutation<Category, Error, UpdateCategoryParams>({
    mutationFn: (params: UpdateCategoryParams) => categoryService.updateCategory(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['categories', variables.providerId, variables.versionId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['category-hierarchy', variables.providerId, variables.versionId] 
      });
      toast.success('Category updated successfully');
    },
    onError: (error) => {
      console.error('Update category error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update category');
    }
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, DeleteCategoryParams>({
    mutationFn: (params: DeleteCategoryParams) => categoryService.deleteCategory(params),
    onSuccess: (_, { providerId, versionId }) => {
      queryClient.invalidateQueries({ 
        queryKey: ['categories', providerId, versionId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['category-hierarchy', providerId, versionId] 
      });
    }
  });
}

export function useUpdateCategoryOrder() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, UpdateCategoryOrderParams>({
    mutationFn: (params: UpdateCategoryOrderParams) => categoryService.updateDisplayOrder(params),
    onSuccess: (_, { providerId, versionId }) => {
      queryClient.invalidateQueries({ 
        queryKey: ['categories', providerId, versionId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['category-hierarchy', providerId, versionId] 
      });
    }
  });
}
